import { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { ContentItem } from '../../types/content';

interface FeatureEntry {
  title: string;
  description: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (item: ContentItem) => void;
  item: ContentItem | null;
  sectionId: string;
}

function parseContentValue(raw: any): any {
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return raw.replace(/^"|"$/g, '');
  }
}

function isFeatureArray(data: any): data is FeatureEntry[] {
  return Array.isArray(data) && data.length > 0 && data.every(
    (item: any) => typeof item === 'object' && item !== null && 'title' in item && 'description' in item
  );
}

export default function MobileContentItemSheet({ isOpen, onClose, onSave, item, sectionId }: Props) {
  const [formData, setFormData] = useState<ContentItem>({
    id: crypto.randomUUID(),
    title: '',
    type: 'text',
    content: '',
    status: 'Draft',
  });
  const [features, setFeatures] = useState<FeatureEntry[]>([]);
  const [useFeatureEditor, setUseFeatureEditor] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (item) {
      const content = parseContentValue(item.content);
      if (isFeatureArray(content)) {
        setFeatures(content.map(f => ({ title: f.title, description: f.description })));
        setUseFeatureEditor(true);
        setFormData({ ...item, content: '' });
      } else {
        setUseFeatureEditor(false);
        setFeatures([]);
        setFormData({
          ...item,
          content: typeof content === 'object' ? JSON.stringify(content, null, 2) : String(content ?? ''),
        });
      }
    } else {
      setUseFeatureEditor(false);
      setFeatures([]);
      setFormData({ id: crypto.randomUUID(), title: '', type: 'text', content: '', status: 'Draft' });
    }
  }, [item, isOpen, sectionId]);

  const handleTypeChange = (type: ContentItem['type']) => {
    setFormData(prev => ({ ...prev, type }));
    if (type === 'data') {
      setUseFeatureEditor(true);
      if (features.length === 0) setFeatures([{ title: '', description: '' }]);
    } else {
      setUseFeatureEditor(false);
    }
  };

  const handleFeatureChange = (index: number, field: keyof FeatureEntry, value: string) => {
    const updated = [...features];
    updated[index] = { ...updated[index], [field]: value };
    setFeatures(updated);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let content: string;
    if (useFeatureEditor) {
      const valid = features.filter(f => f.title.trim() || f.description.trim());
      content = JSON.stringify(valid);
    } else {
      const raw = formData.content;
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          try { JSON.parse(trimmed); content = trimmed; }
          catch { content = JSON.stringify(trimmed); }
        } else {
          content = JSON.stringify(trimmed);
        }
      } else {
        content = raw;
      }
    }
    onSave({ ...formData, content });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-x-0 top-0 bottom-20 z-40 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-golf-gray">{item ? 'Edit Content' : 'Add Content'}</h2>
            <p className="text-xs text-gray-400 mt-0.5">Fill in the details below</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors">
            <XMarkIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter title..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Type</label>
              <select
                value={formData.type}
                onChange={e => handleTypeChange(e.target.value as ContentItem['type'])}
                className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50 appearance-none"
              >
                <option value="text">Text</option>
                <option value="media">Media</option>
                <option value="data">Data</option>
                <option value="champions">Champions</option>
                <option value="gallery">Gallery</option>
                <option value="press">Press</option>
                <option value="committee">Committee</option>
                <option value="testimonials">Testimonials</option>
                <option value="slide">Slide</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Status</label>
              <select
                value={formData.status}
                onChange={e => setFormData(prev => ({ ...prev, status: e.target.value as 'Published' | 'Draft' }))}
                className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50 appearance-none"
              >
                <option value="Draft">Draft</option>
                <option value="Published">Published</option>
              </select>
            </div>
          </div>

          {useFeatureEditor ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Features</label>
                <button
                  type="button"
                  onClick={() => setFeatures(prev => [...prev, { title: '', description: '' }])}
                  className="flex items-center gap-1 text-xs font-medium text-golf-orange active:opacity-70"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Add
                </button>
              </div>
              <div className="space-y-3">
                {features.map((feature, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-xl p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-400">#{idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => setFeatures(prev => prev.filter((_, i) => i !== idx))}
                        className="p-1 text-red-400 active:text-red-600"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <input
                      type="text"
                      placeholder="Feature title"
                      value={feature.title}
                      onChange={e => handleFeatureChange(idx, 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-2 focus:outline-none focus:border-golf-orange bg-white"
                    />
                    <textarea
                      placeholder="Feature description"
                      value={feature.description}
                      onChange={e => handleFeatureChange(idx, 'description', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-golf-orange bg-white resize-none"
                    />
                  </div>
                ))}
                {features.length === 0 && (
                  <p className="text-center text-xs text-gray-400 py-4">No features. Tap "Add" to get started.</p>
                )}
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Content</label>
              <textarea
                value={formData.content}
                onChange={e => setFormData(prev => ({ ...prev, content: e.target.value }))}
                rows={6}
                placeholder="Enter content..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50 font-mono resize-none"
              />
            </div>
          )}

          <div className="pb-2" />
        </form>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="content-form"
            onClick={handleSubmit}
            className="flex-1 py-3 rounded-xl bg-golf-orange text-white text-sm font-semibold active:bg-golf-orange/90 transition-colors"
          >
            {item ? 'Save Changes' : 'Add Content'}
          </button>
        </div>
      </div>
    </div>
  );
}
